import React, { useEffect, useRef, useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Reveal } from "./Reveal";

const GoogleG = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const Stars = () => (
  <span className="flex gap-1" aria-label="5 star review">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
    ))}
  </span>
);

const REVIEWS = [
  {
    name: "Emma Wiley",
    quote:
      "Brilliant accountants to work with — I highly recommend them. I am so grateful to Abbie. I had been fumbling along doing mine and my husband's accounts for these past few years of self-employment before seeking the advice of Rosevale. Abbie advised that there were legitimate expenses that I hadn't been claiming for, and she explained how to make a claim for these, even going several years back. I submitted the claim and in time was given a rebate from HMRC of £623.88! This was brilliant news, and something I would never have got back had I continued going solo. They are thorough and personable, great to work with and to entrust your business to.",
    highlight: "£623.88!",
  },
  {
    name: "Tom Warne",
    quote:
      "Abbie is a Xero expert and taught me everything I know! She's always willing to help and it's great to have her onboard.",
  },
  {
    name: "Ben Brooks",
    quote:
      "All the staff at Rosevale have always been very accommodating, knowledgable, and helpful of all things to do with our accounts. They have provided excellent advice to help our business operate efficiently and economically.",
  },
  {
    name: "Bradley Malpas",
    quote: "Execellent accountants. Trustworthy and really helpful. Highly recommend!",
  },
];

const ReviewCard = ({ review, index }) => (
  <figure
    data-testid={index < REVIEWS.length ? `review-card-${review.name.toLowerCase().replace(/\s+/g, "-")}` : undefined}
    className="relative shrink-0 w-[320px] sm:w-[420px] rounded-2xl bg-white border border-[#103080]/10 p-7 sm:p-9 ambient-shadow flex flex-col"
  >
    <Quote size={26} className="text-[#40C0E0] mb-5 rotate-180" strokeWidth={1.5} />
    <blockquote className="text-sm sm:text-base text-[#0F172A]/75 leading-relaxed flex-1">
      {review.highlight ? (
        <>
          {review.quote.split(review.highlight)[0]}
          <span className="font-serif font-semibold text-[#103080]">{review.highlight}</span>
          {review.quote.split(review.highlight)[1]}
        </>
      ) : (
        review.quote
      )}
    </blockquote>
    <figcaption className="mt-6 pt-5 border-t border-[#103080]/10 flex items-center justify-between gap-3">
      <div>
        <p className="font-semibold text-[#103080] text-sm">{review.name}</p>
        <p className="text-[10px] text-[#0F172A]/50 uppercase tracking-[0.15em] mt-1">Google review</p>
      </div>
      <Stars />
    </figcaption>
  </figure>
);

const SPEEDS = [
  { id: "slow", label: "Slow", pxPerSec: 28 },
  { id: "normal", label: "Normal", pxPerSec: 60 },
  { id: "fast", label: "Fast", pxPerSec: 120 },
];

const wrap = (v, half) => ((v % half) + half) % half;

// Auto-scrolls the track; supports pause, arrow nudges (eased) and drag/swipe.
const useReviewScroller = (trackRef, pxPerSec, paused) => {
  const offset = useRef(0);
  const target = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(now - last, 64) / 1000;
      last = now;
      const half = el.scrollWidth / 2 || 1;
      if (target.current !== null) {
        const diff = target.current - offset.current;
        if (Math.abs(diff) < 0.5) { offset.current = target.current; target.current = null; }
        else offset.current += diff * Math.min(1, dt * 8);
      } else if (!paused && !reduce && !drag.current) {
        offset.current += pxPerSec * dt;
      }
      offset.current = wrap(offset.current, half);
      el.style.transform = `translate3d(${-offset.current}px,0,0)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [trackRef, pxPerSec, paused]);

  const nudge = (dir) => {
    const card = trackRef.current?.querySelector("figure");
    const stepPx = card ? card.getBoundingClientRect().width + 20 : 440;
    target.current = (target.current ?? offset.current) + dir * stepPx;
  };

  const onPointerDown = (e) => {
    drag.current = { x: e.clientX, start: offset.current, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    target.current = null;
    offset.current = drag.current.start - dx;
  };
  const onPointerUp = () => { drag.current = null; };

  return { nudge, dragHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
};

export const TestimonialCards = () => {
  const trackRef = useRef(null);
  const [speed, setSpeed] = useState("normal");
  const [paused, setPaused] = useState(false);
  const { nudge, dragHandlers } = useReviewScroller(trackRef, SPEEDS.find((s) => s.id === speed).pxPerSec, paused);
  const iconBase = "w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-300";
  const iconBtn = `${iconBase} border-[#103080]/15 bg-white text-[#103080] hover:bg-[#103080] hover:text-white`;
  const iconBtnActive = `${iconBase} border-[#103080] bg-[#103080] text-white hover:bg-[#0090D0] hover:border-[#0090D0]`;

  return (
  <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-36 overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Google Reviews</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 sm:mb-14">
          <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-xl">
            Don't take our word for it.
          </h2>
          <div className="flex flex-col sm:items-end gap-4">
            <div className="flex items-center gap-3">
              <GoogleG />
              <Stars />
              <span className="text-sm font-medium text-[#0F172A]/70">5.0 · 12 reviews on Google</span>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="reviews-speed-control" role="group" aria-label="Review scroll controls">
              <button type="button" onClick={() => nudge(-1)} aria-label="Previous review" data-testid="reviews-prev" className={iconBtn}><ChevronLeft size={18} /></button>
              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                aria-label={paused ? "Play reviews" : "Pause reviews"}
                aria-pressed={paused}
                data-testid="reviews-play-pause"
                className={paused ? iconBtnActive : iconBtn}
              >
                {paused ? <Play size={16} className="ml-0.5" /> : <Pause size={16} />}
              </button>
              <button type="button" onClick={() => nudge(1)} aria-label="Next review" data-testid="reviews-next" className={iconBtn}><ChevronRight size={18} /></button>
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#0F172A]/45 ml-3 mr-1">Speed</span>
              {SPEEDS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSpeed(s.id)}
                  aria-pressed={speed === s.id}
                  data-testid={`reviews-speed-${s.id}`}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors duration-300 ${
                    speed === s.id
                      ? "bg-[#103080] border-[#103080] text-white"
                      : "bg-white border-[#103080]/15 text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </div>

    <Reveal delay={0.15}>
      <div className="relative" data-testid="reviews-marquee">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 z-10 bg-gradient-to-r from-[#FDFBF7] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 z-10 bg-gradient-to-l from-[#FDFBF7] to-transparent" />
        <div
          ref={trackRef}
          {...dragHandlers}
          className="flex w-max items-stretch gap-5 pr-5 will-change-transform cursor-grab active:cursor-grabbing select-none touch-pan-y"
          data-testid="reviews-track"
        >
          {[...REVIEWS, ...REVIEWS].map((r, i) => (
            <ReviewCard key={`${r.name}-${i}`} review={r} index={i} />
          ))}
        </div>
      </div>
    </Reveal>
  </section>
  );
};
